/**
 * Complimentary ("comp") access — frontend mirror of the backend
 * `shared/comp.ts` helper.
 *
 * A comp grant (`workspace.comp`) makes a workspace fully free + unlimited with
 * NO Stripe involvement (beta testers, partner/comp accounts). Every UI gate
 * that would otherwise prompt for a card or block a paid feature consults THIS
 * helper rather than reading `comp.enabled` directly, so an `expiresAt` in the
 * past degrades the grant cleanly and consistently.
 *
 * The grant is written ONLY by the `setWorkspaceComp` superadmin callable; it
 * is a function-only Firestore field.
 */

/** Timestamp shapes the field can carry once read from Firestore. */
type CompTimestamp =
  | Date
  | { toMillis: () => number }
  | { seconds: number; nanoseconds?: number }
  | string
  | number;

export interface WorkspaceComp {
  /** Master switch. When false (or the object absent) the workspace is normal. */
  enabled: boolean;
  /** Free-text audit note (cohort, partner name…). */
  reason?: string;
  /** Superadmin uid that granted it. */
  grantedBy: string;
  /** When it was granted. */
  grantedAt: CompTimestamp;
  /** Optional auto-expiry; once past, the grant is inactive. */
  expiresAt?: CompTimestamp;
}

/**
 * Coerce a Firestore Timestamp | Date | epoch-ms | ISO string into ms, or null
 * when the value can't be interpreted.
 */
function toMillis(value: CompTimestamp | null | undefined): number | null {
  if (value == null) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : null;
  }
  const maybe = value as { toMillis?: () => number; seconds?: number };
  if (typeof maybe.toMillis === 'function') return maybe.toMillis();
  if (typeof maybe.seconds === 'number') return maybe.seconds * 1000;
  return null;
}

/**
 * Whether a workspace currently holds an ACTIVE complimentary grant.
 *
 * True iff `comp.enabled === true` AND (`expiresAt` absent OR still in the
 * future). Absent/false/expired grants all return false.
 *
 * @param comp The workspace's `comp` block (may be undefined).
 * @param now Clock override for tests; defaults to the current time.
 */
export function isWorkspaceCompActive(
  comp: WorkspaceComp | null | undefined,
  now: Date = new Date()
): boolean {
  if (!comp || comp.enabled !== true) return false;
  if (comp.expiresAt == null) return true;
  const expiresMs = toMillis(comp.expiresAt);
  if (expiresMs == null) return true; // unparseable expiry → treat as no expiry
  return expiresMs > now.getTime();
}
